import React from 'react'
import { match, P } from 'ts-pattern'

type State = 'initial' | 'off' | 'on' | 'turnOff' | 'turnOn'

export const useToggleAnimation = (
  toggle: boolean,
  styles: [
    offClassName: string,
    turnOnClassName: string,
    onClassName: string,
    turnOffClassName: string,
  ],
) => {
  const [offClassName, turnOnClassName, onClassName, turnOffClassName] = styles

  const [state, setState] = React.useState<State>('initial')

  React.useEffect(() => {
    setState((state) =>
      match<[State, boolean], State>([state, toggle])
        .with(['initial', true], () => 'on')
        .with(['initial', false], () => 'off')
        .with([P.not('initial'), true], () => 'turnOn')
        .with([P.not('initial'), false], () => 'turnOff')
        .exhaustive(),
    )
  }, [toggle])

  return React.useMemo(() => {
    const animationClassName = match(state)
      .with('initial', () => undefined)
      .with('off', () => offClassName)
      .with('turnOn', () => turnOnClassName)
      .with('on', () => onClassName)
      .with('turnOff', () => turnOffClassName)
      .exhaustive()

    const animationEndHandler: React.AnimationEventHandler<
      HTMLElement
    > = () => {
      setState((state) =>
        match<State, State>(state)
          .with('turnOn', () => 'on')
          .with('turnOff', () => 'off')
          .otherwise(() => state),
      )
    }

    return [animationClassName, animationEndHandler] as const
  }, [offClassName, onClassName, state, turnOffClassName, turnOnClassName])
}
